import {
  AdTagCampaign,
  AdTagExperimentVariant,
  Brand,
  Macro,
  Operator,
  Tracking
} from "@fresh8/types/src";
import { Offer } from "@fresh8/types/src/betData";
import * as generateLinks from "@fresh8/ad-data-composition/src/lib/generateLinks";
import {
  computed,
  ComputedRef,
  inject,
  provide,
  Ref,
  WritableComputedRef
} from "vue";
const searchParams = new URLSearchParams(window.location.search);

const offerDefined = (offer: Offer | undefined) => {
  return (
    offer &&
    ((offer.body.length || 0) > 0 ||
      (offer.header.length || 0) > 0 ||
      (offer.buttonText.length || 0) > 0 ||
      (offer.terms.length || 0) > 0)
  );
};

const operatorHasOffer = (
  operatorId: string,
  offers: Record<string, Offer>
) => {
  const operatorOffer = offers?.[operatorId];
  return offerDefined(operatorOffer);
};

export function getOffer(
  adTagExperimentVariant: AdTagExperimentVariant | undefined,
  adTagOperator: Operator | undefined,
  selectedCampaign: AdTagCampaign | undefined,
  brandOffers: Record<string, Offer> | undefined,
  tracking: Tracking | undefined,
  macros: Macro[],
  previewDefaultOffer: Offer | undefined,
  brandTeam: string | undefined,
  isPreview: boolean | undefined
): Offer | undefined {
  const brandOperatorOffer =
    adTagOperator &&
    brandOffers &&
    operatorHasOffer(adTagOperator?._id || "", brandOffers) &&
    brandOffers?.[adTagOperator?._id || ""];

  const experimentOffer =
    offerDefined(
      adTagExperimentVariant?.operators[adTagOperator?._id || ""]?.offer
    ) && adTagExperimentVariant?.operators[adTagOperator?._id || ""]?.offer;

  const campaignOffer =
    offerDefined(selectedCampaign?.campaignContent.offer) &&
    selectedCampaign?.campaignContent.offer;

  // if we have an variant or campaign selected we show its operator offer.
  const campaignOrExperimentOffer: Offer | undefined | false =
    experimentOffer || campaignOffer;

  let offer: Offer | undefined = undefined;

  // If it's the preview and there's an operator offer just reutrn it without doing
  // any of the extra processing to save time
  if (brandOperatorOffer && isPreview) {
    return brandOperatorOffer;
  }

  // If we have a brand offer for the selected operator set that first
  if (brandOperatorOffer) {
    offer = { ...brandOperatorOffer };
  }

  // If there's an experiment or campaign offer then override the brand offer
  if (campaignOrExperimentOffer) {
    offer = { ...campaignOrExperimentOffer };

    // Copy the link from the brand if the campaign or experiment offer doesn't have a link
    if ((!offer.link || offer.link === "") && brandOperatorOffer) {
      offer.link = brandOperatorOffer.link;
    }

    // Copy the terms from the brand if the campaign or experiment offer doesn't have a link
    if ((!offer.terms || offer.terms === "") && brandOperatorOffer) {
      offer.terms = brandOperatorOffer.terms;
    }
  }

  // If there is no offer link set use the ad tag fallback link
  if (offer && tracking?.fallbackUrl) {
    offer.link = tracking?.fallbackUrl;
  }

  // check if theres a campaign and apply the capaigns target url
  if (
    offer &&
    selectedCampaign &&
    selectedCampaign?.targetUrl &&
    selectedCampaign?.targetUrl !== "" &&
    (selectedCampaign.campaignContent.offer?.link || "") === ""
  ) {
    offer.link = selectedCampaign.targetUrl;
  }

  // Add the tracking to the link
  if (offer) {
    const clickUrl = generateLinks.getClickUrl(
      tracking?.clickThroughUrl,
      tracking?.clickThroughBehaviour,
      searchParams.get("clickUrl") || ""
    );

    const affiliateId = generateLinks.getAffiliateId(
      tracking?.affiliateCode || "",
      searchParams.get("aff") || "",
      brandTeam || "",
      macros
    );

    offer = generateLinks.offer(offer, {
      affiliateId: affiliateId || "",
      clickUrl: clickUrl || "",
      operatorKey: adTagOperator?.key || ""
    });
  }

  // If preview has a default offer and there's no brand
  // offer set return the preview default offer.
  // This is a place holder offer display in the previews
  if (!offer && previewDefaultOffer) {
    return previewDefaultOffer;
  }

  return offer;
}

export function createOffer(
  brand: Ref<Brand>,
  adTagExperimentVariant: Ref<AdTagExperimentVariant | undefined>,
  selectedCampaign: Ref<AdTagCampaign | undefined>,
  adTagOperator:
    | WritableComputedRef<Operator | undefined>
    | Ref<Operator | undefined>,
  tracking: Ref<Tracking | undefined>,
  macros: Ref<Macro[]>,
  previewDefaultOffer: Ref<Offer | undefined>,
  isPreview: Ref<boolean | undefined>
): { offer: ComputedRef<Offer | undefined> } {
  const brandOffer = computed(() => brand.value.offers);
  const brandTeam = computed(() => brand.value.team);

  const offer = computed(() => {
    return getOffer(
      adTagExperimentVariant.value,
      adTagOperator.value,
      selectedCampaign.value,
      brandOffer.value,
      tracking.value,
      macros.value,
      previewDefaultOffer.value,
      brandTeam.value,
      isPreview.value
    );
  });

  return {
    offer
  };
}

export type OfferContext = ReturnType<typeof createOffer>;

export const OfferSymbol = Symbol("OfferCompSymbol");

export function provideOffer(offerContext: OfferContext): OfferContext {
  provide(OfferSymbol, offerContext);
  return offerContext;
}

export function injectOffer(): OfferContext | undefined {
  return inject<OfferContext>(OfferSymbol);
}
